import { useState, useEffect, useMemo, useRef } from 'react'
import type { DragEvent } from 'react'
import { useSession } from '@/context/AuthContext'
import { useToast } from '@/context/ToastContext'
import { useClickOutside } from '@/lib/useClickOutside'
import { CreateProjectModal } from '@/components/CreateProjectModal'
import {
  Plus, Search, X, ThumbsUp, MoreHorizontal, Trash2,
  Lightbulb, ChevronDown, Loader2, User, History, FolderPlus, Tag, Clock,
} from 'lucide-react'
import { VisibilityPicker, VisibilityBadge } from '@/components/VisibilityPicker'
import type { Idea, IdeaStatus, IdeaStatusLog } from '@/lib/types'
import { BOARD_STATUSES, allowedNextStatusesFor, statusMoveDenial, ideaTargets } from '@/lib/ideaStatus'
import { canModifyItem, hasCrossDeptView, SUPER_DEPARTMENT, type Visibility } from '@/lib/roles'
import { deptColor, DEPARTMENTS } from '@/lib/departments'
import { formatDate } from '@/lib/utils'

// ─── constants ────────────────────────────────────────────────────────────────
const CATEGORIES = [
  'Fitur Baru', 'Peningkatan Proses', 'Budaya & HR',
  'Teknologi', 'Marketing', 'Produk', 'Umum',
]

const STATUS_CONFIG: Record<IdeaStatus, { label: string; badge: string; dot: string }> = {
  pending:     { label: 'Pending',    badge: 'bg-[#F4F3F0] text-[#6B6860] border border-[#E0DFD8]',   dot: 'bg-[#C0BDB6]' },
  new:         { label: 'Baru',       badge: 'bg-[#EFF6FF] text-[#2563EB] border border-[#BFCFFD]',   dot: 'bg-[#2563EB]' },
  reviewing:   { label: 'Dikaji',     badge: 'bg-[#FEF3C7] text-[#B45309] border border-[#FDE68A]',   dot: 'bg-[#F59E0B]' },
  approved:    { label: 'Disetujui',  badge: 'bg-[#DCFCE7] text-[#16A34A] border border-[#BBF7D0]',   dot: 'bg-[#16A34A]' },
  rejected:    { label: 'Ditolak',    badge: 'bg-[#FEF2F2] text-[#DC2626] border border-[#FECACA]',   dot: 'bg-[#DC2626]' },
  implemented: { label: 'Diterapkan', badge: 'bg-[#1A1916] text-white border border-[#1A1916]',        dot: 'bg-[#1A1916]' },
}

// Kolom Kanban — satu kolom per status, urutannya ikut BOARD_STATUSES supaya papan
// dan aturan pemindahan tidak bisa berbeda: Baru → Dikaji → Disetujui → Ditolak → Diterapkan.
const KANBAN_COLUMNS: { key: IdeaStatus; label: string }[] = BOARD_STATUSES.map((key) => ({
  key, label: STATUS_CONFIG[key].label,
}))

/** Ide + posisinya di papan — bentuk yang dipakai semua pengecekan wewenang status. */
const boardOf = (idea: Idea) => ({
  status: idea.status,
  submitterEmail: idea.submitterEmail,
  targets: idea.targets,
  targetScope: idea.targetScope,
  targetDepartment: idea.targetDepartment,
})

const SORT_OPTIONS = [
  { key: 'recent', label: 'Terbaru' },
  { key: 'votes',  label: 'Tervote' },
  { key: 'title',  label: 'Judul A–Z' },
] as const
type SortKey = typeof SORT_OPTIONS[number]['key']

const AVATAR_COLORS = ['#6366F1','#F59E0B','#16A34A','#EF4444','#8B5CF6','#EC4899','#14B8A6','#F97316']
function avatarColor(name: string) {
  let h = 0; for (let i = 0; i < name.length; i++) h += name.charCodeAt(i)
  return AVATAR_COLORS[h % AVATAR_COLORS.length]
}

// Tint badge asal ide — warna konsisten per topik (asal sama → warna sama)
const SOURCE_TINTS = [
  { bg: '#EEF2FF', border: '#C7D2FE', text: '#4338CA' }, // indigo
  { bg: '#FEF3C7', border: '#FDE68A', text: '#B45309' }, // amber
  { bg: '#CCFBF1', border: '#99F6E4', text: '#0F766E' }, // teal
  { bg: '#FFE4E6', border: '#FECDD3', text: '#BE123C' }, // rose
  { bg: '#DCFCE7', border: '#BBF7D0', text: '#15803D' }, // green
  { bg: '#E0F2FE', border: '#BAE6FD', text: '#0369A1' }, // sky
  { bg: '#F3E8FF', border: '#E9D5FF', text: '#7E22CE' }, // violet
]
function sourceTint(s: string) {
  let h = 0; for (let i = 0; i < s.length; i++) h += s.charCodeAt(i)
  return SOURCE_TINTS[h % SOURCE_TINTS.length]
}

function SourceBadge({ text, truncate = true }: { text: string; truncate?: boolean }) {
  const t = sourceTint(text)
  return (
    <span title={`Asal ide: ${text}`}
      className="inline-flex items-center gap-1.5 max-w-full pl-1.5 pr-2 py-0.5 rounded-md border text-[10px] font-body font-semibold"
      style={{ backgroundColor: t.bg, borderColor: t.border, color: t.text }}>
      <Tag className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={2.25} />
      <span className={truncate ? 'truncate' : ''}>{text}</span>
    </span>
  )
}

// ─── sub-components ───────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: IdeaStatus }) {
  const c = STATUS_CONFIG[status]
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-body font-semibold ${c.badge}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
      {c.label}
    </span>
  )
}

// Penanda ide terbatas (bukan 'public').
const ConfidentialBadge = VisibilityBadge

function Avatar({ name, size = 'sm' }: { name: string; size?: 'sm' | 'md' | 'lg' }) {
  const cls = size === 'lg' ? 'w-10 h-10 text-sm' : size === 'md' ? 'w-7 h-7 text-xs' : 'w-5 h-5 text-[9px]'
  return (
    <div className={`${cls} rounded-full flex items-center justify-center text-white font-bold font-body flex-shrink-0`}
      style={{ backgroundColor: avatarColor(name) }} title={name}>
      {name.charAt(0).toUpperCase()}
    </div>
  )
}

// ─── IdeaCard ─────────────────────────────────────────────────────────────────
function IdeaCard({ idea, dragging, canDrag, onVote, onDelete, onSubmitterChange, onCreateProject, onOpenDetail, onDragStart, onDragEnd }: {
  idea: Idea
  dragging: boolean
  canDrag: boolean
  onVote: (id: string) => void
  onDelete: (id: string) => void
  onSubmitterChange: (id: string, submitter: string) => void
  onCreateProject: (idea: Idea) => void
  onOpenDetail: (idea: Idea) => void
  onDragStart: (e: DragEvent<HTMLDivElement>) => void
  onDragEnd: () => void
}) {
  const [showMenu, setShowMenu] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [history, setHistory] = useState<IdeaStatusLog[] | null>(null)
  const [editingSubmitter, setEditingSubmitter] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const historyPanelRef = useRef<HTMLDivElement>(null)
  const histBtnRef = useRef<HTMLButtonElement>(null)
  useClickOutside(menuRef, () => setShowMenu(false), showMenu)

  // Tutup panel riwayat saat klik di luar panel & tombolnya
  useEffect(() => {
    if (!showHistory) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (historyPanelRef.current?.contains(t) || histBtnRef.current?.contains(t)) return
      setShowHistory(false)
    }
    document.addEventListener('pointerdown', onDown)
    return () => document.removeEventListener('pointerdown', onDown)
  }, [showHistory])

  // Wewenang & badge departemen
  const { data: session } = useSession()
  const viewer = { role: session?.user?.role, department: session?.user?.department, email: session?.user?.email }
  const canModify = canModifyItem(viewer, { department: idea.department, ownerEmail: idea.submitterEmail })
  // "Buat Project dari ide" adalah jalan menuju Diterapkan, jadi ikut aturan papan:
  // hanya muncul kalau viewer ini memang boleh memindahkan ide ke kolom Diterapkan.
  const canImplement = allowedNextStatusesFor(boardOf(idea), viewer).includes('implemented')
  const crossDept = !!idea.department && idea.department !== (session?.user?.department ?? null)
  const dc = deptColor(idea.department)

  const saveSubmitter = (value: string) => {
    const v = value.trim()
    if (v && v !== idea.submitter) onSubmitterChange(idea.id, v)
    setEditingSubmitter(false)
  }

  const toggleHistory = async () => {
    const next = !showHistory
    setShowHistory(next)
    if (next && history === null) {
      try {
        const res = await fetch(`/api/ideas/${idea.id}/history`)
        setHistory(res.ok ? await res.json() : [])
      } catch {
        setHistory([])
      }
    }
  }

  return (
    <div
      draggable={canDrag && !editingSubmitter}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      title={canDrag ? 'Seret ke kolom lain untuk ubah status' : undefined}
      className={`group relative bg-white border border-[#E8E7E3] rounded-xl p-4 flex flex-col hover:shadow-md hover:-translate-y-0.5 transition-[transform,box-shadow,border-color,opacity] duration-200 ${
        canDrag ? 'cursor-grab active:cursor-grabbing' : ''
      } ${dragging ? 'opacity-40' : ''}`}
    >
      {/* Top: category + history + menu (status dipindah ke kolom kanban / drag & drop) */}
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex items-center gap-1.5 min-w-0">
          <span title={idea.category} className="text-[9px] font-body font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F4F3F0] text-[#6B6860] min-w-0 truncate max-w-full">
            {idea.category}
          </span>
          <ConfidentialBadge v={idea.visibility} />
          {crossDept && (
            <span className="text-[8px] font-body font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border flex-shrink-0"
              style={{ backgroundColor: dc.bg, color: dc.text, borderColor: dc.border }}
              title={`Departemen: ${idea.department}`}>
              {idea.department}
            </span>
          )}
          {/* Tujuan rute — penting sejak ide bisa dilempar lintas unit: tanpa ini
              penerimanya melihat ide asing tanpa tahu kenapa ide itu ada di papannya. */}
          <span title={`Ditujukan ke ${ideaTargets(idea).join(', ')}`}
            className="text-[8px] font-body font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-[#F4F3F0] text-[#6B6860] border border-[#E0DFD8] flex-shrink-0 truncate max-w-[130px]">
            Ke {ideaTargets(idea).join(', ')}
          </span>
          {idea.estimatedDays != null && (
            <span title={`Estimasi realisasi ${idea.estimatedDays} hari`}
              className="flex items-center gap-1 text-[9px] font-body font-semibold px-1.5 py-0.5 rounded-full bg-[#F0F0FA] text-[#6366F1] flex-shrink-0">
              <Clock className="w-2.5 h-2.5" />~{idea.estimatedDays}h
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Riwayat status (timeline) — panel-nya di-render di bawah, anchor ke kartu */}
          <button ref={histBtnRef} onClick={toggleHistory} title="Riwayat approval"
            className="p-1 rounded-lg text-[#C8C7BF] hover:text-[#6B6860] hover:bg-[#F4F3F0] transition-colors">
            <History className="w-3.5 h-3.5" />
          </button>

          {/* More menu — tampil kalau ada aksi yang relevan */}
          {(canImplement || canModify) && (
            <div ref={menuRef} className="relative">
              <button onClick={() => setShowMenu(!showMenu)}
                className={`p-1 rounded-lg text-[#C8C7BF] hover:text-[#6B6860] hover:bg-[#F4F3F0] transition-colors ${showMenu ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}>
                <MoreHorizontal className="w-3.5 h-3.5" />
              </button>
              {showMenu && (
                <div className="absolute right-0 top-7 z-30 bg-white border border-[#E8E7E3] rounded-xl shadow-xl py-1.5 w-44 animate-fade-in">
                  {canImplement && (
                    <button onClick={() => { onCreateProject(idea); setShowMenu(false) }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#1A1916] hover:bg-[#F4F3F0] font-body">
                      <FolderPlus className="w-3.5 h-3.5" /> Buat Project dari ide
                    </button>
                  )}
                  {canModify && (
                    <button onClick={() => { onDelete(idea.id); setShowMenu(false) }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#DC2626] hover:bg-[#FEF2F2] font-body">
                      <Trash2 className="w-3.5 h-3.5" /> Hapus
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Panel Riwayat Approval — absolute di dalam kartu (lebar muat, anti-kepotong) */}
      {showHistory && (
        <div ref={historyPanelRef}
          className="absolute right-3 top-12 z-50 w-60 max-w-[calc(100%-1.5rem)] bg-white border border-[#E8E7E3] rounded-xl shadow-xl p-3 animate-fade-in">
          <p className="text-[9px] uppercase tracking-wider text-[#C0BDB6] font-body font-semibold mb-2.5">Riwayat Approval</p>
          {history === null ? (
            <p className="text-[11px] text-[#AEADA8] font-body">Memuat…</p>
          ) : history.length === 0 ? (
            <p className="text-[11px] text-[#AEADA8] font-body">Belum ada riwayat</p>
          ) : (
            <ol className="space-y-2.5">
              {history.map((h) => (
                <li key={h.id} className="flex items-start gap-2">
                  <span className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${STATUS_CONFIG[h.status].dot}`} />
                  <div className="min-w-0">
                    <p className="text-[11px] font-body font-semibold text-[#1A1916] leading-tight">{STATUS_CONFIG[h.status].label}</p>
                    <p className="text-[10px] text-[#8A8A85] font-body leading-tight mt-0.5">
                      {h.changedByName || 'Tak diketahui'} · {formatDate(h.createdAt)}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      {/* Badge asal ide — dipakai ide lama yang dulu dibuat berkelompok. */}
      {idea.sourceSession && (
        <div className="mb-2 flex">
          <SourceBadge text={idea.sourceSession} />
        </div>
      )}

      {/* Title — klik untuk lihat detail penuh */}
      <h3 onClick={() => onOpenDetail(idea)}
        className="font-display text-[14px] font-bold text-[#1A1916] leading-snug mb-2 line-clamp-2 flex-shrink-0 cursor-pointer hover:text-[#6366F1] transition-colors">
        {idea.title}
      </h3>

      {/* Description — klik untuk lihat detail penuh */}
      <p onClick={() => onOpenDetail(idea)}
        className="text-[12px] text-[#8A8A85] font-body leading-relaxed line-clamp-3 flex-1 mb-3 cursor-pointer">
        {idea.description || '—'}
      </p>

      {/* Approver status terkini */}
      {idea.statusBy && (
        <p className="flex items-center gap-1.5 text-[10px] text-[#AEADA8] font-body mb-3">
          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_CONFIG[idea.status].dot}`} />
          <span className="truncate">
            {STATUS_CONFIG[idea.status].label} oleh <span className="font-semibold text-[#8A8A85]">{idea.statusBy}</span>
            {idea.statusAt ? ` · ${formatDate(idea.statusAt)}` : ''}
          </span>
        </p>
      )}

      {/* Footer */}
      <div className="pt-3 border-t border-[#F4F3F0] flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          {editingSubmitter ? (
            <input
              autoFocus
              defaultValue={idea.submitter}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveSubmitter((e.target as HTMLInputElement).value)
                if (e.key === 'Escape') setEditingSubmitter(false)
              }}
              onBlur={(e) => saveSubmitter(e.target.value)}
              className="text-[11px] px-1.5 py-0.5 w-28 bg-[#F8F7F3] border border-[#C8C7BF] rounded-md font-body text-[#1A1916] focus:outline-none focus:border-[#1A1916]"
            />
          ) : (
            <>
              <Avatar name={idea.submitter} size="sm" />
              {canModify ? (
                <button
                  onClick={() => setEditingSubmitter(true)}
                  title="Klik untuk ubah pengaju"
                  className="text-[11px] text-[#6B6860] font-body truncate hover:text-[#1A1916] transition-colors"
                >
                  {idea.submitter}
                </button>
              ) : (
                <span className="text-[11px] text-[#6B6860] font-body truncate">{idea.submitter}</span>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            onClick={() => onVote(idea.id)}
            className="flex items-center gap-1 text-[11px] text-[#8A8A85] hover:text-[#6366F1] font-body transition-colors group/vote"
          >
            <ThumbsUp className="w-3.5 h-3.5 group-hover/vote:scale-110 transition-transform" />
            <span className="font-semibold">{idea.votes}</span>
          </button>
          <span className="text-[10px] text-[#C0BDB6] font-body">{formatDate(idea.createdAt)}</span>
        </div>
      </div>
    </div>
  )
}

// ─── SubmitIdeModal — manual sepenuhnya (tanpa AI) ───────────────────────────
export interface SubmitIdeaPayload {
  title: string
  description: string
  category: string
  submitter: string
  estimatedDays: number | null
  visibility: Visibility
  viewerEmails: string[]
  /** Departemen tujuan — bebas lintas unit, boleh lebih dari satu. */
  targets: string[]
}

function SubmitIdeModal({ onClose, onSubmitOne }: {
  onClose: () => void
  onSubmitOne: (data: SubmitIdeaPayload) => Promise<void>
}) {
  // Default terbuka se-BU; non-Member boleh mempersempit lewat picker.
  const [visibility, setVisibility] = useState<Visibility>('public')
  const [viewerEmails, setViewerEmails] = useState<string[]>([])
  const inputClass = 'w-full px-3.5 py-2.5 bg-[#F8F7F3] border border-[#E0DFD8] rounded-xl text-[#111110] text-sm font-body placeholder:text-[#C8C7BF] focus:outline-none focus:border-[#111110] focus:bg-white transition-all'

  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [category, setCategory] = useState(CATEGORIES[0])
  const [customCategory, setCustomCategory] = useState('')
  const [estimatedDays, setEstimatedDays] = useState('')
  // Rute ide bebas lintas unit: BU → BU, BU → HOLDING, divisi internal → BU, dan boleh
  // beberapa tujuan sekaligus. Server memberlakukan aturan yang sama.
  const [targets, setTargets] = useState<string[]>([SUPER_DEPARTMENT])
  const [savingOne, setSavingOne] = useState(false)

  const toggleTarget = (dept: string) =>
    setTargets(prev => prev.includes(dept) ? prev.filter(d => d !== dept) : [...prev, dept])

  // "" / invalid → null; selain itu integer ≥ 0.
  const parseDays = (v: string): number | null => {
    const n = Math.floor(Number(v))
    return v.trim() !== '' && Number.isFinite(n) && n >= 0 ? n : null
  }

  // Pengaju dikunci ke akun Google yang login
  const { data: session } = useSession()
  const me = session?.user?.name || session?.user?.email || ''

  const handleSaveOne = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !targets.length || savingOne) return
    setSavingOne(true)
    const finalCategory = category === '__lainnya__' ? (customCategory.trim() || 'Umum') : category
    try {
      await onSubmitOne({
        title: title.trim(), description: description.trim(), category: finalCategory,
        submitter: me || 'Tanpa Nama', estimatedDays: parseDays(estimatedDays),
        visibility, viewerEmails, targets,
      })
      onClose()
    } finally { setSavingOne(false) }
  }

  const canSave = !!title.trim()
    && targets.length > 0
    && (category !== '__lainnya__' || !!customCategory.trim())

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />

      <div className="relative w-full max-w-[560px] bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 animate-slide-up max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#F2F1EC] flex-shrink-0">
          <h2 className="font-display text-[15px] font-bold text-[#1A1916]">Submit Ide</h2>
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#111110] transition-colors flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <form id="manual-form" onSubmit={handleSaveOne} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
                Judul Ide <span className="text-[#DC2626] font-normal text-xs">*wajib</span>
              </label>
              <input type="text" value={title} onChange={e => setTitle(e.target.value)}
                placeholder="Singkat dan jelas, cth: Dark Mode untuk Dashboard"
                className={inputClass} autoFocus />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
                Deskripsi <span className="text-[#AEADA8] font-normal text-xs">opsional</span>
              </label>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder="Jelaskan latar belakang, manfaat, atau cara implementasinya..."
                rows={4} className={`${inputClass} resize-none leading-relaxed`} />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
                Estimasi Realisasi <span className="text-[#AEADA8] font-normal text-xs">opsional</span>
              </label>
              <div className="flex items-center gap-2">
                <input type="number" min={0} inputMode="numeric"
                  value={estimatedDays} onChange={e => setEstimatedDays(e.target.value)}
                  placeholder="cth: 14"
                  className={`${inputClass} w-32`} />
                <span className="text-sm text-[#6B6860] font-body">hari</span>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">
                Tujukan ke <span className="text-[#DC2626] font-normal text-xs">*wajib</span>
              </label>
              <div className="flex flex-wrap gap-1.5">
                {DEPARTMENTS.map(dept => {
                  const active = targets.includes(dept)
                  return (
                    <button key={dept} type="button" onClick={() => toggleTarget(dept)}
                      aria-pressed={active}
                      className={`px-3 py-1.5 rounded-xl text-xs font-body font-semibold border transition-colors ${
                        active
                          ? 'bg-[#1A1916] text-white border-[#1A1916]'
                          : 'bg-white text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'}`}>
                      {dept}
                    </button>
                  )
                })}
              </div>
              <p className="mt-1.5 text-xs text-[#AEADA8] font-body">
                {targets.length === 0
                  ? 'Pilih minimal satu tujuan.'
                  : `Yang mengkaji: ${targets.map(t => t === SUPER_DEPARTMENT ? 'HOLDING' : `Head ${t}`).join(', ')}.`}
              </p>
            </div>

            <VisibilityPicker value={visibility} onChange={setVisibility}
              viewerEmails={viewerEmails} onViewerEmailsChange={setViewerEmails} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">Kategori</label>
                <select value={category} onChange={e => setCategory(e.target.value)} className={inputClass}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  <option value="__lainnya__">Lainnya…</option>
                </select>
                {category === '__lainnya__' && (
                  <input
                    type="text"
                    value={customCategory}
                    onChange={e => setCustomCategory(e.target.value)}
                    placeholder="Tulis kategori sendiri…"
                    autoFocus
                    className={`${inputClass} mt-2`}
                  />
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#111110] font-body mb-1.5">Pengaju</label>
                <div className="flex items-center gap-2 px-3.5 py-2.5 bg-[#F4F3F0] border border-[#E0DFD8] rounded-xl">
                  <User className="w-3.5 h-3.5 text-[#AEADA8] flex-shrink-0" />
                  <span className="text-sm font-body text-[#6B6860] truncate">{me || 'Akun kamu'}</span>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F2F1EC] flex items-center justify-between flex-shrink-0">
          <p className="text-xs text-[#AEADA8] font-body">Status default: Baru</p>
          <div className="flex items-center gap-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-body text-[#8A8A85] hover:text-[#111110] transition-colors">
              Batal
            </button>
            <button type="submit" form="manual-form" disabled={!canSave || savingOne}
              className="flex items-center gap-2 px-5 py-2 bg-[#111110] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2A2A28] disabled:opacity-40 disabled:cursor-not-allowed transition-all">
              {savingOne ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Submit Ide
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── IdeaDetailModal — lihat ide lengkap (teks penuh, tidak terpotong) ─────────
function IdeaDetailModal({ idea, onClose, onVote }: {
  idea: Idea
  onClose: () => void
  onVote: (id: string) => void
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] animate-fade-in" onClick={onClose} />

      <div className="relative w-full max-w-[580px] max-h-[85vh] flex flex-col bg-white border border-[#E0DFD8] rounded-2xl shadow-2xl shadow-black/15 animate-slide-up">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-6 py-4 border-b border-[#F2F1EC] flex-shrink-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] font-body font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#F4F3F0] text-[#6B6860]">
              {idea.category}
            </span>
            <StatusBadge status={idea.status} />
          </div>
          <button onClick={onClose} aria-label="Tutup"
            className="p-2 -mr-2 -mt-1 rounded-xl hover:bg-[#F2F1EC] text-[#AEADA8] hover:text-[#111110] transition-colors flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body (scroll) */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {idea.sourceSession && (
            <div className="mb-3 flex">
              <SourceBadge text={idea.sourceSession} truncate={false} />
            </div>
          )}
          <h2 className="font-display text-xl font-bold text-[#1A1916] leading-snug mb-3">
            {idea.title}
          </h2>
          <p className="text-sm text-[#3A3A36] font-body leading-relaxed whitespace-pre-wrap">
            {idea.description || '—'}
          </p>
          <p className="text-[11px] text-[#8A8A85] font-body mt-4">
            Ditujukan ke <span className="font-semibold text-[#6B6860]">{ideaTargets(idea).join(', ')}</span>
            {idea.department ? <> · dari <span className="font-semibold text-[#6B6860]">{idea.department}</span></> : null}
          </p>
          {idea.statusBy && (
            <p className="flex items-center gap-1.5 text-[11px] text-[#AEADA8] font-body mt-5 pt-4 border-t border-[#F4F3F0]">
              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${STATUS_CONFIG[idea.status].dot}`} />
              {STATUS_CONFIG[idea.status].label} oleh <span className="font-semibold text-[#8A8A85]">{idea.statusBy}</span>
              {idea.statusAt ? ` · ${formatDate(idea.statusAt)}` : ''}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#F2F1EC] flex items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <Avatar name={idea.submitter} size="md" />
            <div className="min-w-0">
              <p className="text-sm font-body font-semibold text-[#1A1916] truncate">{idea.submitter}</p>
              <p className="text-[11px] text-[#AEADA8] font-body">{formatDate(idea.createdAt)}</p>
            </div>
          </div>
          <button onClick={() => onVote(idea.id)}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-[#E8E7E3] text-[#6B6860] text-sm font-body hover:border-[#6366F1] hover:text-[#6366F1] active:scale-[0.98] transition-all flex-shrink-0">
            <ThumbsUp className="w-4 h-4" />
            <span className="font-semibold">{idea.votes}</span>
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function IdeasPage() {
  const [ideas, setIdeas] = useState<Idea[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortKey>('recent')
  const [showSortMenu, setShowSortMenu] = useState(false)
  const [showSubmitModal, setShowSubmitModal] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [selectedSubmitter, setSelectedSubmitter] = useState<string | null>(null)
  const [createFromIdea, setCreateFromIdea] = useState<Idea | null>(null)
  const [detailId, setDetailId] = useState<string | null>(null)
  const { data: session } = useSession()
  const { toast } = useToast()
  const me = session?.user?.name || session?.user?.email || ''
  const myEmail = session?.user?.email ?? null
  const holding = hasCrossDeptView(session?.user ?? {})
  const [selectedDept, setSelectedDept] = useState<string | null>(null)
  const deptOptions = useMemo(
    () => Array.from(new Set([...DEPARTMENTS, ...ideas.map((i) => i.department).filter(Boolean) as string[]])),
    [ideas],
  )

  // ── Badge notif ide baru per BU (client-side, per-user via localStorage) ──
  // lastSeen[dep] = timestamp terakhir Holding melihat kolom ide BU tsb.
  const SEEN_KEY = 'idea_bu_seen'
  const [lastSeen, setLastSeen] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem(SEEN_KEY) || '{}') } catch { return {} }
  })
  const markSeen = (dep: string) => {
    setLastSeen((prev) => {
      const next = { ...prev, [dep]: new Date().toISOString() }
      try { localStorage.setItem(SEEN_KEY, JSON.stringify(next)) } catch { /* ignore */ }
      return next
    })
  }
  // Klik tombol BU tertentu → filter + tandai BU itu sudah dilihat (badge nol).
  // "Semua" (null) → hanya lepas filter, badge per-BU tetap agar Holding tahu BU mana yg baru.
  const selectDept = (dep: string | null) => {
    setSelectedDept(dep)
    if (dep) markSeen(dep)
  }
  // Jumlah ide 'new' (masuk) yg belum dilihat, per BU.
  const unseenByDept = useMemo(() => {
    const m: Record<string, number> = {}
    for (const i of ideas) {
      if (i.status !== 'new') continue
      const dep = i.department ?? ''
      const seen = lastSeen[dep]
      if (!seen || new Date(i.createdAt).getTime() > new Date(seen).getTime()) {
        m[dep] = (m[dep] ?? 0) + 1
      }
    }
    return m
  }, [ideas, lastSeen])
  const unseenTotal = useMemo(
    () => Object.values(unseenByDept).reduce((a, b) => a + b, 0),
    [unseenByDept],
  )

  useEffect(() => {
    fetch('/api/ideas').then(r => r.json()).then(data => {
      setIdeas(Array.isArray(data) ? data : [])
      setLoading(false)
    }).catch(() => setLoading(false))
  }, [])

  // Lingkup filter konteks (kategori + pengaju) — dasar stats, breakdown status, & daftar
  const scoped = useMemo(() => {
    let list = ideas
    if (holding && selectedDept) list = list.filter(i => i.department === selectedDept)
    if (selectedCategory)  list = list.filter(i => i.category === selectedCategory)
    if (selectedSubmitter) list = list.filter(i => i.submitter === selectedSubmitter)
    return list
  }, [ideas, selectedCategory, selectedSubmitter, holding, selectedDept])

  // Aggregate stats — otomatis ikut filter kategori/pengaju
  const stats = useMemo(() => ({
    total: scoped.length,
    new: scoped.filter(i => i.status === 'new').length,
    reviewing: scoped.filter(i => i.status === 'reviewing').length,
    approved: scoped.filter(i => i.status === 'approved').length,
    rejected: scoped.filter(i => i.status === 'rejected').length,
    implemented: scoped.filter(i => i.status === 'implemented').length,
    totalVotes: scoped.reduce((s, i) => s + i.votes, 0),
  }), [scoped])

  // Breakdown kategori — ikut filter pengaju (kalau ada) supaya konsisten
  const categoryBreakdown = useMemo(() => {
    const src = selectedSubmitter ? ideas.filter(i => i.submitter === selectedSubmitter) : ideas
    const map = new Map<string, number>()
    for (const idea of src) map.set(idea.category, (map.get(idea.category) ?? 0) + 1)
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])
  }, [ideas, selectedSubmitter])

  // Breakdown pengaju — jumlah ide per orang (analisis + filter), ikut filter kategori
  const submitterBreakdown = useMemo(() => {
    const src = selectedCategory ? ideas.filter(i => i.category === selectedCategory) : ideas
    const map = new Map<string, number>()
    for (const idea of src) map.set(idea.submitter, (map.get(idea.submitter) ?? 0) + 1)
    return Array.from(map.entries()).sort((a, b) => b[1] - a[1])
  }, [ideas, selectedCategory])

  // Visible ideas (semua status — dipecah per kolom kanban di render)
  const visible = useMemo(() => {
    let list = [...scoped]
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(i => i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q) || i.submitter.toLowerCase().includes(q) || (i.sourceSession ?? '').toLowerCase().includes(q))
    }
    if (sort === 'votes')  list.sort((a, b) => b.votes - a.votes)
    if (sort === 'title')  list.sort((a, b) => a.title.localeCompare(b.title))
    if (sort === 'recent') list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    return list
  }, [scoped, search, sort])

  const handleVote = async (id: string) => {
    const res = await fetch(`/api/ideas/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'vote' }),
    })
    if (!res.ok) return
    const updated = await res.json()
    if (updated?.id) setIdeas(prev => prev.map(i => i.id === id ? updated : i))
  }

  const handleStatusChange = async (id: string, status: IdeaStatus) => {
    // Status "Diterapkan" → buka form buat project DULU. Status baru berubah jadi
    // "Diterapkan" KALAU project benar-benar dibuat (lihat handleProjectCreatedFromIdea).
    // Kalau form dibatalkan → status tetap di semula.
    if (status === 'implemented') {
      const idea = ideas.find(i => i.id === id)
      if (idea) setCreateFromIdea(idea)
      return
    }
    // Optimistic — wewenang sudah dicek di UI & divalidasi ulang server
    const nowIso = new Date().toISOString()
    setIdeas(prev => prev.map(i => i.id === id ? { ...i, status, statusBy: me || i.statusBy, statusAt: nowIso } : i))
    const res = await fetch(`/api/ideas/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status, changedByName: me, changedByEmail: myEmail }),
    })
    if (res.ok) {
      const updated: Idea = await res.json()
      setIdeas(prev => prev.map(i => i.id === id ? updated : i))
    } else {
      // Gagal (mis. transisi tidak valid) → sinkron ulang dari server
      const list = await fetch('/api/ideas').then(r => r.json()).catch(() => null)
      if (Array.isArray(list)) setIdeas(list)
    }
  }

  // Setelah project dibuat dari ide → ide jadi "diterapkan" (kalau belum di sana)
  const handleProjectCreatedFromIdea = async (idea: Idea) => {
    if (idea.status === 'implemented') return
    const nowIso = new Date().toISOString()
    setIdeas(prev => prev.map(i => i.id === idea.id ? { ...i, status: 'implemented', statusBy: me || i.statusBy, statusAt: nowIso } : i))
    await fetch(`/api/ideas/${idea.id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: 'implemented', changedByName: me, changedByEmail: myEmail }),
    })
  }

  // ── Drag & drop antar kolom status (HTML5 native, tanpa library) ──
  // Penerima ide bebas geser ke kolom mana pun, kiri atau kanan; pengaju cuma
  // bolak-balik Baru ↔ Diterapkan. Aturannya di lib, dipakai server & UI sama-sama.
  const viewer = { role: session?.user?.role, department: session?.user?.department, email: myEmail }
  const [dragId, setDragId] = useState<string | null>(null)
  const [overCol, setOverCol] = useState<IdeaStatus | null>(null)
  const canDragIdea = (idea: Idea) => allowedNextStatusesFor(boardOf(idea), viewer).length > 0
  const dropTo = (status: IdeaStatus) => {
    const id = dragId
    setDragId(null); setOverCol(null)
    const idea = id ? ideas.find((i) => i.id === id) : null
    if (!idea || idea.status === status) return
    const denial = statusMoveDenial(viewer, boardOf(idea), status)
    if (denial) {
      toast(denial, 'error')
      return
    }
    handleStatusChange(idea.id, status)
  }

  const handleDelete = async (id: string) => {
    setIdeas(prev => prev.filter(i => i.id !== id))
    await fetch(`/api/ideas/${id}`, { method: 'DELETE' })
  }

  const handleSubmitterChange = async (id: string, submitter: string) => {
    setIdeas(prev => prev.map(i => i.id === id ? { ...i, submitter } : i))
    await fetch(`/api/ideas/${id}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ submitter }),
    })
  }

  const handleSubmit = async (data: SubmitIdeaPayload) => {
    const res = await fetch('/api/ideas', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })
    const idea = await res.json()
    if (!res.ok || !idea?.id) throw new Error(idea?.error ?? 'Gagal submit ide')
    // Ide siapa pun langsung masuk board sebagai "Baru" — tanpa antre ACC.
    setIdeas(prev => [idea, ...prev])
    toast('Ide masuk board dengan status "Baru".', 'info')
  }

  return (
    <div className="min-h-screen bg-[#F5F4F0]">

      {/* Header */}
      <header className="bg-white border-b border-[#E8E7E3] sticky top-0 z-30">
        <div className="px-6 flex items-center gap-4" style={{ height: '52px' }}>
          <span className="font-display text-[15px] font-bold text-[#1A1916] flex-shrink-0">Ide Board</span>
          <div className="flex-1 max-w-sm mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#C0BDB6] pointer-events-none" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Cari ide, deskripsi, atau nama..."
              className="w-full pl-9 pr-8 py-1.5 bg-[#F4F3F0] border border-transparent rounded-xl text-sm font-body text-[#1A1916] placeholder:text-[#C0BDB6] focus:outline-none focus:bg-white focus:border-[#E0DFD8] transition-all" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2"><X className="w-3.5 h-3.5 text-[#AEADA8]" /></button>}
          </div>
          <button onClick={() => setShowSubmitModal(true)}
            className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A] active:scale-[0.98] transition-all">
            <Plus className="w-3.5 h-3.5" /> Submit Ide
          </button>
        </div>
      </header>

      <main className="px-6 py-5 space-y-4">

        {/* Insight panel */}
        <div className="bg-white border border-[#E8E7E3] rounded-2xl p-5">
          {/* Stats row — 2 angka utama (netral) + breakdown status tenang */}
          <div className="flex items-center gap-6 flex-wrap mb-5">
            {/* Primary */}
            {[
              { label: 'Total Ide',  value: stats.total },
              { label: 'Total Vote', value: stats.totalVotes },
            ].map((s) => (
              <div key={s.label}>
                <p className="font-display font-bold leading-none text-[#1A1916]" style={{ fontSize: '1.75rem' }}>
                  {loading ? '—' : s.value}
                </p>
                <p className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body mt-1">{s.label}</p>
              </div>
            ))}

            <div className="w-px h-10 bg-[#F0EFE9]" />

            {/* Breakdown status — titik warna kecil, angka netral. Urutannya sama
                dengan kolom papan di bawah supaya mata tidak perlu pindah urutan. */}
            <div className="flex items-center gap-x-5 gap-y-3 flex-wrap">
              {[
                { label: 'Baru',       value: stats.new,         color: '#2563EB' },
                { label: 'Dikaji',     value: stats.reviewing,   color: '#F59E0B' },
                { label: 'Disetujui',  value: stats.approved,    color: '#16A34A' },
                { label: 'Ditolak',    value: stats.rejected,    color: '#DC2626' },
                { label: 'Diterapkan', value: stats.implemented, color: 'var(--kpi-neutral)' },
              ].map((s) => {
                const pctVal = stats.total > 0 ? Math.round((s.value / stats.total) * 100) : 0
                return (
                  <div key={s.label} className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                    <span className="font-display text-[17px] font-bold text-[#1A1916] leading-none tabular-nums">
                      {loading ? '—' : s.value}
                    </span>
                    <div className="leading-tight">
                      <span className="block text-[10px] uppercase tracking-wider text-[#8A8A85] font-body">{s.label}</span>
                      <span className="block text-[10px] text-[#C0BDB6] font-body">{pctVal}%</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Category chips */}
          {categoryBreakdown.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pt-3 border-t border-[#F4F3F0]">
              <span className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body">Kategori</span>
              {categoryBreakdown.map(([cat, count]) => {
                const active = selectedCategory === cat
                return (
                  <button key={cat}
                    onClick={() => setSelectedCategory(active ? null : cat)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-xl border text-[11px] font-body font-medium transition-all ${
                      active ? 'bg-[#1A1916] text-white border-[#1A1916]' : 'bg-[#F8F7F3] text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'
                    }`}>
                    <Lightbulb className="w-3 h-3" />
                    {cat}
                    <span className={`text-[10px] font-bold ${active ? 'opacity-70' : 'text-[#C0BDB6]'}`}>{count}</span>
                  </button>
                )
              })}
            </div>
          )}

          {/* Pengaju chips — jumlah ide per orang + filter */}
          {submitterBreakdown.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap pt-3 mt-1 border-t border-[#F4F3F0]">
              <span className="text-[9px] uppercase tracking-widest text-[#C0BDB6] font-body">Pengaju</span>
              {submitterBreakdown.map(([name, count]) => {
                const active = selectedSubmitter === name
                return (
                  <button key={name}
                    onClick={() => setSelectedSubmitter(active ? null : name)}
                    title={`${name} — ${count} ide`}
                    className={`flex items-center gap-1.5 pl-1 pr-2.5 py-1 rounded-xl border text-[11px] font-body font-medium transition-all ${
                      active ? 'bg-[#1A1916] text-white border-[#1A1916]' : 'bg-[#F8F7F3] text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'
                    }`}>
                    <Avatar name={name} size="sm" />
                    {name}
                    <span className={`text-[10px] font-bold ${active ? 'opacity-70' : 'text-[#C0BDB6]'}`}>{count}</span>
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Filter tombol per Unit Bisnis — hanya Holding (monitoring lintas BU).
            Badge merah = jumlah ide baru (masuk) yang belum dilihat di BU itu. */}
        {holding && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] uppercase tracking-widest text-[#C0BDB6] font-body font-semibold mr-1">Unit Bisnis</span>
            {[null, ...deptOptions].map((dep) => {
              const active = selectedDept === dep
              const count = dep === null ? unseenTotal : (unseenByDept[dep] ?? 0)
              return (
                <button key={dep ?? '__all__'} onClick={() => selectDept(dep)}
                  aria-pressed={active}
                  title={dep === null ? 'Semua unit bisnis' : `Filter ${dep}`}
                  className={`relative flex items-center gap-1.5 px-3.5 py-2 rounded-xl border text-xs font-body font-medium transition-all min-h-[36px] ${
                    active ? 'bg-[#1A1916] text-white border-[#1A1916]' : 'bg-white text-[#6B6860] border-[#E8E7E3] hover:border-[#C8C7BF]'
                  }`}>
                  {dep === null ? 'Semua' : dep}
                  {count > 0 && (
                    <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#DC2626] text-white text-[10px] font-bold leading-none"
                      title={`${count} ide baru belum dilihat`}>
                      {count > 99 ? '99+' : count}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}

        {/* Filter + sort bar */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[11px] uppercase tracking-widest text-[#C0BDB6] font-body font-semibold">Papan Ide</span>

          <div className="flex items-center gap-2">
            {selectedCategory && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1916] text-white rounded-xl text-xs font-body">
                <Lightbulb className="w-3 h-3" />
                {selectedCategory}
                <button onClick={() => setSelectedCategory(null)} className="ml-0.5 opacity-70 hover:opacity-100">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {selectedSubmitter && (
              <div className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1A1916] text-white rounded-xl text-xs font-body">
                <User className="w-3 h-3" />
                {selectedSubmitter}
                <button onClick={() => setSelectedSubmitter(null)} className="ml-0.5 opacity-70 hover:opacity-100">
                  <X className="w-3 h-3" />
                </button>
              </div>
            )}
            {search && <span className="text-xs text-[#A8A8A2] font-body">{visible.length} hasil</span>}

            <div className="relative">
              <button onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-[#E8E7E3] rounded-xl text-xs font-body text-[#6B6860] hover:border-[#C8C7BF] transition-colors">
                {SORT_OPTIONS.find(s => s.key === sort)?.label}
                <ChevronDown className="w-3 h-3 opacity-40" />
              </button>
              {showSortMenu && (<>
                <div className="fixed inset-0 z-10" onClick={() => setShowSortMenu(false)} />
                <div className="absolute right-0 top-9 z-20 bg-white border border-[#E8E7E3] rounded-xl shadow-xl py-1.5 w-36 animate-fade-in">
                  {SORT_OPTIONS.map(s => (
                    <button key={s.key} onClick={() => { setSort(s.key); setShowSortMenu(false) }}
                      className={`w-full flex items-center justify-between px-3.5 py-2 text-xs font-body transition-colors ${sort === s.key ? 'text-[#1A1916] font-semibold bg-[#F4F3F0]' : 'text-[#6B6860] hover:bg-[#F4F3F0]'}`}>
                      {s.label}{sort === s.key && <span>✓</span>}
                    </button>
                  ))}
                </div>
              </>)}
            </div>
          </div>
        </div>

        {/* Ideas grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white border border-[#E8E7E3] rounded-xl p-4 animate-pulse space-y-2.5">
                <div className="h-3 bg-[#F4F3F0] rounded w-1/3" />
                <div className="h-4 bg-[#F4F3F0] rounded w-3/4" />
                <div className="h-3 bg-[#F4F3F0] rounded w-full" />
                <div className="h-3 bg-[#F4F3F0] rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-12 h-12 rounded-2xl bg-white border border-[#E8E7E3] flex items-center justify-center mb-3">
              <Lightbulb className="w-5 h-5 text-[#C0BDB6]" />
            </div>
            <h3 className="font-display text-xl font-bold text-[#1A1916] mb-1.5">Belum ada ide</h3>
            <p className="text-sm text-[#8A8A85] font-body mb-5 max-w-xs">
              {search ? `Tidak ada ide yang cocok dengan "${search}"` : 'Jadilah yang pertama submit ide!'}
            </p>
            {!search && (
              <button onClick={() => setShowSubmitModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#1A1916] text-white rounded-xl text-sm font-medium font-body hover:bg-[#2E2E2A]">
                <Plus className="w-4 h-4" /> Submit Ide Pertama
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 items-start">
            {KANBAN_COLUMNS.map(col => {
              const colIdeas = visible.filter(i => i.status === col.key)
              return (
                <div key={col.key}
                  onDragOver={(e) => { if (dragId) { e.preventDefault(); setOverCol(col.key) } }}
                  onDragLeave={() => setOverCol((c) => (c === col.key ? null : c))}
                  onDrop={(e) => { e.preventDefault(); dropTo(col.key) }}
                  className={`border rounded-2xl p-2.5 flex flex-col gap-2 min-h-[120px] transition-colors ${
                    overCol === col.key ? 'bg-[#F3EEFF] border-[#C7B9F5]' : 'bg-[#FAFAF8] border-[#EEEDE8]'
                  }`}>
                  <div className="flex items-center gap-2 px-1 pb-1.5 border-b border-[#F0EFE9]">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${STATUS_CONFIG[col.key].dot}`} />
                    <span className="text-[11px] uppercase tracking-wider font-body font-semibold text-[#6B6860]">{col.label}</span>
                    <span className="ml-auto text-[10px] font-body font-bold text-[#C0BDB6]">{colIdeas.length}</span>
                  </div>
                  {colIdeas.length === 0 ? (
                    <p className="text-[11px] text-[#C8C7BF] font-body text-center py-6">Belum ada</p>
                  ) : (
                    <div className="flex flex-col gap-2.5">
                      {colIdeas.map(idea => (
                        <IdeaCard key={idea.id} idea={idea}
                          dragging={dragId === idea.id}
                          canDrag={canDragIdea(idea)}
                          onDragStart={(e) => { setDragId(idea.id); e.dataTransfer.effectAllowed = 'move' }}
                          onDragEnd={() => { setDragId(null); setOverCol(null) }}
                          onVote={handleVote}
                          onDelete={handleDelete}
                          onSubmitterChange={handleSubmitterChange}
                          onCreateProject={setCreateFromIdea}
                          onOpenDetail={(i) => setDetailId(i.id)} />
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </main>

      {showSubmitModal && (
        <SubmitIdeModal
          onClose={() => setShowSubmitModal(false)}
          onSubmitOne={handleSubmit}
        />
      )}

      {createFromIdea && (
        <CreateProjectModal
          initialName={createFromIdea.title}
          initialDescription={createFromIdea.description}
          onCreated={() => handleProjectCreatedFromIdea(createFromIdea)}
          onClose={() => setCreateFromIdea(null)}
        />
      )}

      {(() => {
        const detailIdea = detailId ? ideas.find(i => i.id === detailId) ?? null : null
        return detailIdea ? (
          <IdeaDetailModal idea={detailIdea} onVote={handleVote} onClose={() => setDetailId(null)} />
        ) : null
      })()}
    </div>
  )
}
